/**
 * UI slice — selection, view mode, filters, edge selection.
 */

import type { StateCreator } from 'zustand';
import type { ViewMode, NodeFilters } from '../types/adjacency';
import { DEFAULT_FILTERS } from '../types/adjacency';
import type { AdjacencyStoreState } from './store-types';

/** Identifies a selected edge between two nodes. */
export interface SelectedEdge {
  sourceId: string;
  targetId: string;
}

/** Context menu state — shown on right-click of a node. */
export interface ContextMenuState {
  nodeId: string;
  x: number;
  y: number;
}

export interface UiSlice {
  // State
  selectedNodeId: string | null;
  selectedEdge: SelectedEdge | null;
  viewMode: ViewMode;
  filters: NodeFilters;
  contextMenu: ContextMenuState | null;

  // Actions
  selectNode: (id: string | null) => void;
  selectEdge: (edge: SelectedEdge | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setFilters: (filters: Partial<NodeFilters>) => void;
  resetFilters: () => void;
  openContextMenu: (menu: ContextMenuState) => void;
  closeContextMenu: () => void;
}

export const createUiSlice: StateCreator<
  AdjacencyStoreState,
  [],
  [],
  UiSlice
> = (set) => ({
  selectedNodeId: null,
  selectedEdge: null,
  viewMode: 'map',
  filters: { ...DEFAULT_FILTERS },
  contextMenu: null,

  selectNode: (id) => set({ selectedNodeId: id, selectedEdge: null, contextMenu: null }),

  selectEdge: (edge) => set({ selectedEdge: edge }),

  setViewMode: (mode) => set({ viewMode: mode }),

  setFilters: (partial) =>
    set((state) => ({ filters: { ...state.filters, ...partial } })),

  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),

  openContextMenu: (menu) => set({ contextMenu: menu, selectedNodeId: menu.nodeId, selectedEdge: null }),

  closeContextMenu: () => set({ contextMenu: null }),
});
